import {
  addDays,
  differenceInCalendarDays,
  intlFormat,
  intlFormatDistance,
} from 'date-fns';
import { TFunction } from 'next-i18next';

import { Company, CompanyWithReportingLabel } from '@/api/search/search-schema';
import { formatPercentageNumber } from '@/utils/asset/assetUtils';

import type {
  ChipType,
  SearchChip,
} from '../../components/search-chips/SearchChips';
import { getCompanyIndexByAssetName } from '../company-utils/companyUtils';

export type AssetStatus = 'beat' | 'miss' | 'meet' | 'analyzing';

const DAYS_AFTER_TODAY = 4;
const LATER_DAYS = DAYS_AFTER_TODAY + 1;
const RECENTLY_DAYS = -3;

export const createChipsData = (
  t: TFunction,
  companies: Company[],
  locale?: string
): SearchChip[] => {
  const todaysDate = new Date();
  const chipsData: Record<string, SearchChip> = {
    [RECENTLY_DAYS]: {
      label: t('recently'),
      type: 'recently',
      companies: [],
    },
    0: { label: t('today'), companies: [], type: 'weekday' },
    1: { label: t('tomorrow'), companies: [], type: 'weekday' },
    [LATER_DAYS]: { label: t('later'), companies: [], type: 'later' },
  };

  for (let i = 2; i <= DAYS_AFTER_TODAY; i++) {
    const date = addDays(todaysDate, i);
    chipsData[i] = {
      label: intlFormat(date, { weekday: 'long' }, { locale }),
      companies: [],
      type: 'weekday',
    };
  }

  const filledWithCompanies = companies.reduce((result, company) => {
    const reportingDate = new Date(company.reporting_date);
    const diff = differenceInCalendarDays(reportingDate, todaysDate);

    if (company.reported || reportingDate < todaysDate) {
      result[RECENTLY_DAYS].companies.push(company);
    } else if (diff >= RECENTLY_DAYS) {
      const key =
        diff < 0 ? RECENTLY_DAYS : diff <= DAYS_AFTER_TODAY ? diff : LATER_DAYS;
      result[key].companies.push(company);
    }

    return result;
  }, chipsData);

  return Object.keys(filledWithCompanies)
    .sort()
    .map(key => filledWithCompanies[key]);
};

interface FormatReportingDate {
  t: TFunction;
  startDate: Date;
  date: Date;
  locale?: string;
}

export const formatSearchReportingDate = ({
  t,
  date,
  startDate,
  locale,
}: FormatReportingDate) => {
  const diff = differenceInCalendarDays(date, startDate);

  if (diff === 0 && date >= startDate) {
    return t('today');
  }

  if (diff > 0) {
    return diff <= DAYS_AFTER_TODAY
      ? intlFormat(date, { weekday: 'long' }, { locale })
      : intlFormatDistance(date, startDate, { unit: 'day', locale });
  } else {
    return intlFormatDistance(date, startDate, { numeric: 'always', locale });
  }
};

interface FormatChipReportingDate extends Omit<FormatReportingDate, 't'> {
  type?: ChipType;
}

const formatChipReportingDate = ({
  type,
  startDate,
  date,
  locale,
}: FormatChipReportingDate) => {
  if (type === 'weekday') {
    return intlFormat(date, { hour: 'numeric', minute: 'numeric' }, { locale });
  }

  if (type === 'later') {
    return intlFormatDistance(date, startDate, { unit: 'day', locale });
  } else {
    return intlFormatDistance(date, startDate, { numeric: 'always', locale });
  }
};

export const getActiveChip = (
  chipsData: SearchChip[],
  assetName?: string
): SearchChip => {
  if (assetName) {
    const foundChip = chipsData.find(
      c => getCompanyIndexByAssetName(c.companies, assetName) !== -1
    );

    if (foundChip) {
      return foundChip;
    }
  }

  const afterRecentChip = chipsData
    .slice(1)
    .find(chip => !!chip.companies.length);
  return afterRecentChip || chipsData[0];
};

export const isMac = () =>
  typeof navigator !== 'undefined' ? /Mac/.test(navigator.userAgent) : false;

export const getFocusedChip = (
  chips: SearchChip[],
  activeChip: SearchChip,
  key: 'ArrowLeft' | 'ArrowRight'
) => {
  const currentChipIdx = chips.findIndex(c => c === activeChip);

  if (key === 'ArrowLeft') {
    return chips
      .slice(0, currentChipIdx === 0 ? chips.length : currentChipIdx)
      .reverse()
      .find(c => !!c.companies.length) as SearchChip;
  }

  if (currentChipIdx === chips.length - 1) {
    return chips.find(c => !!c.companies.length) as SearchChip;
  }

  return chips
    .slice(currentChipIdx + 1)
    .find(c => !!c.companies.length) as SearchChip;
};

export const mapSearchCompanies = (
  t: TFunction,
  companies: Company[],
  locale?: string
): CompanyWithReportingLabel[] => {
  const startDate = new Date();
  return companies.map(company => ({
    ...company,
    reporting_label: formatSearchReportingDate({
      t,
      date: new Date(company.reporting_date),
      startDate,
      locale,
    }),
  }));
};

export const mapChipCompanies = (
  chip: SearchChip,
  locale?: string
): CompanyWithReportingLabel[] => {
  const startDate = new Date();
  return chip.companies.map(company => {
    return {
      ...company,
      reporting_label: formatChipReportingDate({
        type: chip.type,
        date: new Date(company.reporting_date),
        startDate,
        locale,
      }),
    };
  });
};

export interface SurprisePercentageFormat {
  surprise?: string;
  label: string;
  status?: AssetStatus;
}

export const formatSurprisePercentage = (
  item: CompanyWithReportingLabel,
  t: TFunction
): SurprisePercentageFormat => {
  const surprise = item.surprise_percentage;
  if (!item.reported) {
    return { label: t('common:analyzing'), status: 'analyzing' } as const;
  }
  if (surprise !== undefined) {
    const rounded = formatPercentageNumber(surprise);
    return surprise === 0
      ? {
          surprise: rounded,
          label: t('common:meet'),
          status: 'meet',
        }
      : surprise > 0
      ? {
          surprise: rounded,
          label: t('common:beat'),
          status: 'beat',
        }
      : {
          surprise: rounded,
          label: t('common:miss'),
          status: 'miss',
        };
  }
  return { label: t('common:reported'), status: 'analyzing' } as const;
};
